import React from 'react';
import {
  Typography,
  Container,
  Box,
  Divider,
} from '@mui/material';
import Header from '../components/Header';
import Footer from '../components/Footer';

function PrivacyPolicy() {
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Header />
      
      <Container 
        maxWidth="md" 
        sx={{ 
          flex: 1,
          py: 8
        }}
      >
        <Typography 
          variant="h2" 
          component="h1"
          sx={{ 
            fontWeight: 700, 
            mb: 2,
            textAlign: 'center'
          }}
        >
          Privacy Policy
        </Typography>
        
        <Typography 
          variant="body2" 
          sx={{ 
            textAlign: 'center', 
            mb: 6, 
            color: 'text.secondary' 
          }}
        >
          Last Updated: November 17, 2025
        </Typography>

        <Divider sx={{ mb: 6 }} />

        {/* Purpose Section */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 3 }}>
            Purpose
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            The purpose of this Privacy Policy is to inform our clients, partners, and the general public 
            how we protect and handle private and sensitive information. At Actuation GmbH, we are committed to 
            protecting your privacy through stringent privacy practices and adherence to applicable data 
            protection regulations.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            This Privacy Policy describes how we collect, use, disclose, and safeguard your information 
            when you visit our website or use our services. Please read this policy carefully to understand 
            our practices regarding your personal information.
          </Typography>
        </Box>

        {/* Scope and Applicability */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 3 }}>
            Scope and Applicability
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            This Privacy Policy applies to all data and information collected by Actuation GmbH that allows for 
            identification of an individual (Personal Information or Personally Identifiable Information (PII)), 
            including all personal data received from users of our services and website visitors.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            The Personal Information may include but is not limited to names, email addresses, mailing 
            addresses, telephone numbers, and any other information you voluntarily provide to us.
          </Typography>
        </Box>

        {/* Information We Collect */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 3 }}>
            Information We Collect
          </Typography>
          
          <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 2, mt: 3 }}>
            Information You Provide to Us
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We collect information that you voluntarily provide to us when you:
          </Typography>
          <Box component="ul" sx={{ mb: 3, pl: 4 }}>
            <Typography component="li" variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              Contact us through our website or email
            </Typography>
            <Typography component="li" variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              Subscribe to our newsletter or mailing list
            </Typography>
            <Typography component="li" variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              Register for an account or use our services
            </Typography>
            <Typography component="li" variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              Participate in surveys, contests, or promotions
            </Typography>
          </Box>

          <Typography variant="h6" component="h3" sx={{ fontWeight: 600, mb: 2, mt: 3 }}>
            Automatically Collected Information
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            When you visit our website, we may automatically collect certain information about your device, 
            including information about your web browser, IP address, time zone, and some of the cookies 
            that are installed on your device. We collect this information using various technologies, 
            including cookies and web beacons.
          </Typography>
        </Box>

        {/* How We Use Your Information */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 3 }}>
            How We Use Your Information
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We use the information we collect for various purposes, including to:
          </Typography>
          <Box component="ul" sx={{ mb: 3, pl: 4 }}>
            <Typography component="li" variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              Provide, operate, and maintain our services
            </Typography>
            <Typography component="li" variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              Improve, personalize, and expand our services
            </Typography>
            <Typography component="li" variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              Understand and analyze how you use our website and services
            </Typography>
            <Typography component="li" variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              Communicate with you, either directly or through one of our partners
            </Typography>
            <Typography component="li" variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              Send you updates, marketing communications, and other information
            </Typography>
            <Typography component="li" variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              Process your transactions and manage your orders
            </Typography>
            <Typography component="li" variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              Detect and prevent fraud and other illegal activities
            </Typography>
          </Box>
        </Box>

        {/* Cookies and Tracking Technologies */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 3 }}>
            Use of Cookies and Tracking Technologies
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We use cookies and similar tracking technologies to track activity on our website and store 
            certain information. Cookies are files with a small amount of data that may include an anonymous 
            unique identifier. You can instruct your browser to refuse all cookies or to indicate when a 
            cookie is being sent.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We use cookies to:
          </Typography>
          <Box component="ul" sx={{ mb: 3, pl: 4 }}>
            <Typography component="li" variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              Provide a more optimal web experience and customize content
            </Typography>
            <Typography component="li" variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              Remember your preferences and settings
            </Typography>
            <Typography component="li" variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              Track aggregate traffic trends and analyze site usage
            </Typography>
            <Typography component="li" variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              Maintain security and prevent fraud
            </Typography>
          </Box>
        </Box>

        {/* Disclosure of Information */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 3 }}>
            Disclosure of Your Information
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We do not sell or rent your Personal Information to anyone. We may share your information in 
            the following circumstances:
          </Typography>
          <Box component="ul" sx={{ mb: 3, pl: 4 }}>
            <Typography component="li" variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              <strong>Service Providers:</strong> We may share your information with third-party service 
              providers who perform services on our behalf, such as hosting, data analysis, payment 
              processing, and customer service.
            </Typography>
            <Typography component="li" variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              <strong>Legal Requirements:</strong> We may disclose your information if required to do so 
              by law or in response to valid requests by public authorities.
            </Typography>
            <Typography component="li" variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              <strong>Business Transfers:</strong> We may share or transfer your information in connection 
              with, or during negotiations of, any merger, sale of company assets, financing, or acquisition.
            </Typography>
            <Typography component="li" variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              <strong>With Your Consent:</strong> We may disclose your information for any other purpose 
              with your consent.
            </Typography>
          </Box>
        </Box>

        {/* Data Security */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 3 }}>
            Data Security
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We have implemented appropriate technical and organizational security measures designed to 
            protect the security of any personal information we process. However, please note that no 
            electronic transmission over the Internet or information storage technology can be guaranteed 
            to be 100% secure.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            While we strive to use commercially acceptable means to protect your Personal Information, 
            we cannot guarantee its absolute security. We continuously review and enhance our security 
            procedures to protect your information from unauthorized access, use, or disclosure.
          </Typography>
        </Box>

        {/* Data Retention */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 3 }}>
            Data Retention
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We will retain your Personal Information only for as long as is necessary for the purposes 
            set out in this Privacy Policy. We will retain and use your information to the extent necessary 
            to comply with our legal obligations, resolve disputes, and enforce our policies.
          </Typography>
        </Box>

        {/* Your Rights */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 3 }}>
            Your Data Protection Rights
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Depending on your location, you may have the following rights regarding your personal information:
          </Typography>
          <Box component="ul" sx={{ mb: 3, pl: 4 }}>
            <Typography component="li" variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              <strong>Access:</strong> You have the right to request copies of your personal information.
            </Typography>
            <Typography component="li" variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              <strong>Rectification:</strong> You have the right to request that we correct any information 
              you believe is inaccurate or complete information you believe is incomplete.
            </Typography>
            <Typography component="li" variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              <strong>Erasure:</strong> You have the right to request that we erase your personal information, 
              under certain conditions.
            </Typography>
            <Typography component="li" variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              <strong>Restrict Processing:</strong> You have the right to request that we restrict the 
              processing of your personal information, under certain conditions.
            </Typography>
            <Typography component="li" variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              <strong>Object to Processing:</strong> You have the right to object to our processing of 
              your personal information, under certain conditions.
            </Typography>
            <Typography component="li" variant="body1" sx={{ mb: 1, lineHeight: 1.8 }}>
              <strong>Data Portability:</strong> You have the right to request that we transfer the data 
              that we have collected to another organization, or directly to you, under certain conditions.
            </Typography>
          </Box>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            To exercise any of these rights, please contact us using the information provided below.
          </Typography>
        </Box>

        {/* Third-Party Links */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 3 }}>
            Third-Party Links
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Our website may contain links to other sites that are not operated by us. If you click on a 
            third-party link, you will be directed to that third party's site. We strongly advise you to 
            review the Privacy Policy of every site you visit.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We have no control over and assume no responsibility for the content, privacy policies, or 
            practices of any third-party sites or services.
          </Typography>
        </Box>

        {/* Children's Privacy */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 3 }}>
            Children's Privacy
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            Our services are not directed to individuals under the age of 13. We do not knowingly collect 
            personal information from children under 13. If you are a parent or guardian and you are aware 
            that your child has provided us with personal information, please contact us so that we can 
            take necessary action.
          </Typography>
        </Box>

        {/* Changes to Privacy Policy */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 3 }}>
            Changes to This Privacy Policy
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            We may update our Privacy Policy from time to time. We will notify you of any changes by 
            posting the new Privacy Policy on this page and updating the "Last Updated" date at the top 
            of this Privacy Policy.
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            You are advised to review this Privacy Policy periodically for any changes. Changes to this 
            Privacy Policy are effective when they are posted on this page. Your continued use of our 
            services after any modifications to the Privacy Policy will constitute your acknowledgment 
            of the modifications and your consent to abide by the modified Privacy Policy.
          </Typography>
        </Box>

        {/* Contact Information */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 3 }}>
            Contact Information
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            If you have any questions about this Privacy Policy, please contact us:
          </Typography>
          <Typography variant="body1" paragraph sx={{ lineHeight: 1.8 }}>
            <strong>Actuation GmbH</strong><br />
            Heinrichstrasse 118<br />
            8005 Zürich<br />
            Switzerland<br />
            UID: CHE-260.133.410<br />
            Email: <a href="mailto:enquiries@actuation.ch" style={{ color: 'inherit' }}>enquiries@actuation.ch</a>
          </Typography>
        </Box>

        <Divider sx={{ my: 6 }} />

        <Typography 
          variant="body2" 
          sx={{ 
            textAlign: 'center', 
            color: 'text.secondary',
            fontStyle: 'italic'
          }}
        >
          By using our website and services, you acknowledge that you have read and understood this 
          Privacy Policy and agree to its terms.
        </Typography>
      </Container>

      <Footer />
    </Box>
  );
}

export default PrivacyPolicy;

